import React, { useEffect, useState } from "react";

import { useLocalStorage } from "@rehooks/local-storage";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Layout, Menu } from "antd";

import { RootState } from "../../store/root_reducer";

import { MenuItem, MenuItems } from "./items";
import styles from "./styles.module.scss";

export const Sidebar: React.FC = () => {
  const { t } = useTranslation("sidebar");

  const user = useSelector((state: RootState) => state.User);
  const [utcTime, setUTCTime] = useState<string>(moment.utc().format("HH:mm:ss"));
  const [collapsed, setCollapsed] = useLocalStorage("is_sidebar_collapsed", false);

  useEffect(() => {
    const timer = setInterval(() => {
      setUTCTime(moment.utc().format("HH:mm:ss"));
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const onCollapse = () => setCollapsed(!collapsed);

  const filterMenuItems = (menuItem: MenuItem): boolean => {
    if (menuItem.children && menuItem.children.length > 0) {
      return menuItem.permissions.some((perm) => user.permissions.includes(perm));
    }
    return menuItem.permissions.every((perm) => user.permissions.includes(perm));
  };

  const renderItem = (menuItem: MenuItem): React.ReactNode | null => {
    if (menuItem.children) {
      const allowedSubItems = menuItem.children.filter(filterMenuItems);
      if (allowedSubItems.length === 0) {
        return null;
      }
      if (allowedSubItems.length === 1) {
        return renderItem(allowedSubItems[0]);
      }

      return (
        <Menu.SubMenu
          disabled={menuItem.disabled}
          key={`submenu-${menuItem.link}`}
          title={
            <>
              {menuItem.icon}
              <span role="title">{t(menuItem.title)}</span>
            </>
          }
        >
          {allowedSubItems.map(renderItem)}
        </Menu.SubMenu>
      );
    }
    return (
      <Menu.Item key={menuItem.link} disabled={menuItem.disabled}>
        <Link to={menuItem.link} className={styles.item} {...menuItem.linkProps}>
          {menuItem.icon}
          <span role="title">{t(menuItem.title)}</span>
        </Link>
      </Menu.Item>
    );
  };

  return (
    <Layout.Sider
      collapsible
      collapsed={collapsed}
      className={styles.layout}
      width="235"
      onCollapse={onCollapse}
      reverseArrow
      theme="light"
      trigger={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
    >
      <Menu
        theme="light"
        mode="inline"
        className={styles.menu}
        defaultSelectedKeys={[window.location.pathname]}
        selectedKeys={[window.location.pathname]}
      >
        {MenuItems.map((groupItems, idx) => (
          <React.Fragment key={`menu-group-${idx}`}>
            {groupItems.filter(filterMenuItems).map(renderItem)}
            <Menu.Divider />
          </React.Fragment>
        ))}
        <div className={styles.clock}>
          <div>
            <span>UTC</span>
            <time>{utcTime}</time>
          </div>
          <span>{t("clock.description")}</span>
        </div>
      </Menu>
    </Layout.Sider>
  );
};
